import React, { useState } from 'react';
import { AlertCircle, FileText } from 'lucide-react';

/**
 * §8: Subject Rights Requests — stub UI for SUPER_ADMIN.
 * Displays instructions for processing SARs received via the
 * POST /api/subject-requests/* endpoints. Requests are logged
 * in audit_logs with action SUBJECT_REQUEST_RECEIVED.
 */
export default function SASubjectRequestsPage() {
  const [filter, setFilter] = useState('all');

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <FileText className="w-6 h-6 text-blue-900" />
        <h1 className="text-xl font-bold text-gray-900">Subject Rights Requests</h1>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">Manual Processing Required</p>
            <p>Subject rights requests are received via the public API endpoints and logged in the audit trail.
            Identity verification must be conducted offline before processing any request.
            Check the Audit Log for entries with action <code className="bg-amber-100 px-1 rounded">SUBJECT_REQUEST_RECEIVED</code>.</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Request Types</h2>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <span className="text-xs font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded">ACCESS</span>
            <p className="text-sm text-gray-700">Subject requests a copy of all personal data held about them. Respond within 30 days.</p>
          </div>
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <span className="text-xs font-mono bg-green-100 text-green-800 px-2 py-1 rounded">RECTIFICATION</span>
            <p className="text-sm text-gray-700">Subject requests correction of inaccurate personal data. Respond within 30 days.</p>
          </div>
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <span className="text-xs font-mono bg-red-100 text-red-800 px-2 py-1 rounded">ERASURE</span>
            <p className="text-sm text-gray-700">Subject requests deletion. Usually refused under Art. 17(3) for criminal intelligence. Refusal must be recorded and audit-logged.</p>
          </div>
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <span className="text-xs font-mono bg-purple-100 text-purple-800 px-2 py-1 rounded">OBJECTION</span>
            <p className="text-sm text-gray-700">Subject objects to processing. Assess against lawful basis and respond within 30 days.</p>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">API Endpoints</h2>
        <div className="font-mono text-xs text-gray-600 space-y-1">
          <p>POST /api/subject-requests/access</p>
          <p>POST /api/subject-requests/rectification</p>
          <p>POST /api/subject-requests/erasure</p>
          <p>POST /api/subject-requests/objection</p>
        </div>
        <p className="text-xs text-gray-500 mt-3">These endpoints are public (no authentication required). Subjects submit requests with a subject_identifier field.</p>
      </div>
    </div>
  );
}
